<template>
  <div class="article-page">
    <div class="container">
      <div class="article-page__breadcrumbs">
        <Breadcrumbs :breadcrumbs="breadcrumbs" />
      </div>

      <h1 class="article-page__title h2">
        {{ article?.title }}
      </h1>

      <template v-if="Boolean(article?.imageId)">
        <div class="article-page__container-image">
          <nuxt-img
            :src="String(article?.imageId)"
            width="1760px"
            class="article-page__image"
            :alt="`article-${article.id}-${article.title}-image-${article.imageId}`"
          />
        </div>
      </template>

      <div class="article-page__content">
        <div class="--left">
          <client-only>
            <Share />
          </client-only>

          <div class="--space" />
          <OtherNews
            :article-id="article?.id"
            :article-category-id="article?.categoryId"
          />
        </div>
        <div class="--right">
          <cms-page
            v-if="article?.cmsPageId"
            :page-id="article?.cmsPageId"
            :hide-head="true"
            :content-only="true"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import useSitisArticle from '#sitis/composables/useSitisArticle';
import Breadcrumbs from '~/components/UI/Breadcrumbs.vue';
import Share from '~/components/articles/Share.vue';
import OtherNews from '~/components/articles/OtherNews.vue';
import { createError, useRequestURL, useServerSeoMeta } from '#imports';
import { appConfigStore as useAppConfigStore } from '#sitis/stores/modules/app-store';
import { storeToRefs } from 'pinia';

const $route = useRoute();
const { getArticle } = useSitisArticle();

const {
  data: article,
  error: articleError,
  execute: articlesList
} = useLazyAsyncData($route.fullPath, () => getArticle(), {
  immediate: false
});
const { setSEOTitles } = useSitisCMSPage();
const url = useRequestURL();
const productImage = useImage();
const appConfig = useAppConfigStore();
const { config } = storeToRefs(appConfig);

await getArticleFn();
initSeoMeta();

const breadcrumbs = computed(() => [
  { title: 'Главная', path: '/' },
  { title: 'Статьи', path: '/articles' },
  { title: article.value?.title, path: $route.fullPath }
]);

function getStableRandom(seed: number, min: number, max: number): number {
  // Используем ID статьи как seed для стабильности
  const hash = seed.toString().split('').reduce((acc, char) => {
    return ((acc << 5) - acc) + char.charCodeAt(0);
  }, 0);
  return Math.abs(hash) % (max - min + 1) + min;
}

function getStableRatingValue(seed: number): number {
  // Генерируем рейтинг от 4.5 до 5.0 с шагом 0.1
  const hash = seed.toString().split('').reduce((acc, char) => {
    return ((acc << 5) - acc) + char.charCodeAt(0);
  }, 0);
  const index = Math.abs(hash) % 6; // 0-5 для 6 значений (4.5, 4.6, 4.7, 4.8, 4.9, 5.0)
  return 4.5 + (index * 0.1);
}

function getStableReviewCount(seed: number): number {
  // Генерируем количество отзывов от 100 до 2000
  const hash = seed.toString().split('').reduce((acc, char) => {
    return ((acc << 5) - acc) + char.charCodeAt(0);
  }, 0);
  return Math.abs(hash) % (2000 - 100 + 1) + 100;
}

const articleJsonLd: ComputedRef<any> = computed(() => {
  if (!article.value) return null;

  const articleId = article.value.id || 0;
  const shareCount = getStableRandom(articleId, 20, 60);
  const ratingValue = getStableRatingValue(articleId);
  const reviewCount = getStableReviewCount(articleId);

  const articleImageUrl: any = article.value.imageId ? productImage(String(article.value.imageId)) : '';
  const imageInfo: any = articleImageUrl ? { '@type': 'ImageObject', url: articleImageUrl, width: 1760, height: 990 } : undefined;

  const logoUrl = config.value?.logoFront || '';
  const logoWidth = 200; const logoHeight = 200;

  const datePublished = article.value.publishedAt ? new Date(article.value.publishedAt).toISOString() : new Date().toISOString();
  const dateModified = article.value.updatedAt ? new Date(article.value.updatedAt).toISOString() : datePublished;
  const authorName = 'София';
  const canonicalUrl = url.href;

  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': canonicalUrl
    },
    headline: article.value.title || '',
    image: imageInfo,
    datePublished: datePublished,
    dateModified: dateModified,
    author: {
      '@type': 'Person',
      name: authorName
    },
    publisher: {
      '@type': 'Organization',
      name: "MiaSofia",
      logo: logoUrl ? {
        '@type': 'ImageObject',
        url: logoUrl,
        width: logoWidth,
        height: logoHeight
      } : undefined
    },
    description: String(article.value.seoDescription || '').substring(0, 150),
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: ratingValue,
      reviewCount: reviewCount
    },
    interactionStatistic: [
      {
        '@type': 'InteractionCounter',
        interactionType: {
          '@type': 'ShareAction'
        },
        userInteractionCount: shareCount
      },
      {
        '@type': 'InteractionCounter',
        interactionType: {
          '@type': 'CommentAction'
        },
        userInteractionCount: 0
      }
    ]
  };
});
const allJsonLd = computed(() => {
  const schemas = [];
  if (articleJsonLd.value) {
    schemas.push(articleJsonLd.value);
  }
  return schemas.length > 0 ? schemas : null;
});
useJsonld(() => allJsonLd.value);

function initSeoMeta () {
  const title = article.value?.seoTitle || article.value?.title || '';
  const description = article.value?.seoDescription || '';

  setSEOTitles({
    seoTitle: title,
    seoDescription: description
  });

  useServerSeoMeta({
    ogImage: () => productImage(String(article.value?.imageId))
  });
}

async function getArticleFn () {
  await articlesList();

  if (!article.value) {
    throw createError({
      statusCode: 404,
      statusMessage: 'Статья не найдена'
    });
  }
}

</script>

<style lang="scss">
.article-page__title {
	margin-bottom: 32px;
}
.article-page__image {
	margin-bottom: 64px;
	border-radius: 30px;
}
.article-page__content {
	display: flex;

	.--left {
		width: 100%;
		max-width: 320px;
		margin-right: 131px;

		.--space {
			margin-top: 38px;
		}
	}

	.--right {
		flex: 1;
		max-width: 850px;
	}

	h5.title-block {
		color: #989898;
	}

	h3.title-block {
		font-weight: 600;
	}

	.accordion-gallery-block {
		padding: 0;
	}

	.cms-block-image-images {
		width: 100%;
		max-width: initial;
		border-radius: 30px;
		position: relative;
		overflow: hidden;

		img {
			width: 100%;
		}
	}
}

@media (max-width: 1439px) {
  .article-page__content {
    .--left {
      margin-right: 64px;
    }
    .--right {
      max-width: 796px;
    }
  }
}
@media (max-width: 1359px) {
  .article-page__content {
    .--left {
      margin-right: 32px;
    }
    .--right {
      max-width: 640px;
    }
  }
}
@media (max-width: 1023px) {
  .article-page {
    padding-top: 33px;
  }
  .article-page__breadcrumbs {
    display: none;
  }
  .article-page__title {
    margin-bottom: 24px;
  }
  .article-page__image {
    position: absolute;
    top: 0; left: 0;
    width: 100%; height: 100%;
    object-fit: cover;
    border-radius: initial;
    margin: 0;
  }
  .article-page__container-image {
    display: flex;
    margin: 0 -20px;
    max-width: calc(100% + 40px);
    border-radius: 24px;
    position: relative;
    overflow: hidden;
    margin-bottom: 23px;

    &:after {
      content: "";
      float: left;
      padding-top: 61%;
    }
  }
  .article-page__content {
    flex-direction: column-reverse;

    .cms-block-stub p {
      line-height: 150%;
    }
    .--left {
      margin-top: 61px;
    }
    .--left .--space {
      margin-top: 49px;
    }
    .--right {
      max-width: initial;
    }
  }
}
</style>
